import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { IRowProps } from './QuoteSectionBodyTotalPrice'
import { useLocale } from '../../../../../../../../hooks/use-locale'
import {
  CurrencyEnum,
  QuoteSectionClientProposalPriceFragment,
} from '../../../../../../../../lib/graphql'
import { decimalPrice, percentage } from '../../../../../../../../utils'

interface IUseQuoteTotalRowsParams {
  price: QuoteSectionClientProposalPriceFragment
  currency: CurrencyEnum | undefined
}

interface UseQuoteTotalRowsReturn {
  totalRows: IRowProps[]
  totalVATRows: IRowProps[]
}

export const useQuoteTotalRows = ({
  price,
  currency,
}: IUseQuoteTotalRowsParams): UseQuoteTotalRowsReturn => {
  const intl = useIntl()
  const { locale } = useLocale()

  const totalRows = useMemo<IRowProps[]>(() => {
    return [
      {
        label: intl.formatMessage({
          id: 'ui.useQuoteTotalRows.totalGrossAmount',
          defaultMessage:
            'Total Brut de vos prestations (montant affiché au client)',
        }),
        values: {
          HT: decimalPrice(
            price.totalUser?.priceWithDiscount?.priceWithoutVat ?? 0,
            { currency },
          ),
          TTC: decimalPrice(
            price.totalUser?.priceWithDiscount?.priceWithVat ?? 0,
            { currency },
          ),
        },
        textColor: 'text-[#0CBC8B]',
      },
      {
        label: intl.formatMessage({
          id: 'ui.useQuoteTotalRows.nabooCommission',
          defaultMessage: 'Commission Naboo (facturée séparément)',
        }),
        values: {
          HT: decimalPrice(
            price.totalUser?.feesWithDiscount?.ownerFeesPrice
              ?.priceWithoutVat ?? 0,
            { currency },
          ),
          TTC: decimalPrice(
            price.totalUser?.feesWithDiscount?.ownerFeesPrice?.priceWithVat ??
              0,
            { currency },
          ),
        },
      },
      {
        label: intl.formatMessage({
          id: 'ui.useQuoteTotalRows.totalNet',
          defaultMessage:
            'Total Net de vos prestations (montant qui vous est reversé)',
        }),
        values: {
          HT: decimalPrice(
            price.totalUser?.priceWithDiscountAndOwnerFees?.priceWithoutVat ??
              0,
            { currency },
          ),
          TTC: decimalPrice(
            price.totalUser?.priceWithDiscountAndOwnerFees?.priceWithVat ?? 0,
            { currency },
          ),
        },
        textColor: 'text-[#0CBC8B]',
      },
    ]
  }, [price, currency])

  const totalVATRows = useMemo<IRowProps[]>(() => {
    const vatRows: IRowProps[] =
      price.totalPerVatRates?.map((vatRate) => {
        return {
          label: intl.formatMessage(
            {
              id: 'ui.useQuoteTotalRows.totalGrossAmountVAT',
              defaultMessage: `Total brut des prestations à {vatRate} de TVA`,
            },
            {
              vatRate: percentage({
                rate: vatRate.priceWithDiscount?.vatRate,
                locale,
              }),
            },
          ),
          values: {
            HT: decimalPrice(vatRate.priceWithDiscount?.priceWithoutVat, {
              currency,
            }),
            TTC: decimalPrice(vatRate.priceWithDiscount?.priceWithVat, {
              currency,
            }),
          },
        }
      }) ?? []

    const totalOwnerRow: IRowProps = {
      label: intl.formatMessage({
        id: 'ui.useQuoteTotalRows.totalGrossAmount',
        defaultMessage:
          'Total Brut de vos prestations (montant affiché au client)',
      }),
      values: {
        HT: decimalPrice(
          price.totalUser?.priceWithDiscount?.priceWithoutVat ?? 0,
          { currency },
        ),
        TTC: decimalPrice(
          price.totalUser?.priceWithDiscount?.priceWithVat ?? 0,
          { currency },
        ),
      },
      textColor: 'text-[#0CBC8B]',
    }

    return [...vatRows, totalOwnerRow]
  }, [price, currency])

  return { totalRows, totalVATRows }
}
